// src/StateController.js
import { logger } from './Logger.js';

export const GAME_STATES = {
  MENU: 'MENU',
  ENCOUNTER: 'ENCOUNTER',
  FEEDBACK: 'FEEDBACK',
  ROSTER: 'ROSTER',
  REPORT: 'REPORT',
  CASE_SELECT: 'CASE_SELECT',
  SPECIALTY_SELECT: 'SPECIALTY_SELECT'
};

export class StateController {
  constructor(uiElements, callbacks = {}) {
    this.ui = uiElements;
    this.currentState = GAME_STATES.MENU;
    this.callbacks = callbacks; // { onEnterEncounter, onEnterMenu, renderCaseSelection }
    
    // Time Warp 10x: Real-time detection with accelerated progression
    this.realStartTime = Date.now();
    this.hospitalStartTime = Date.now(); 
    this.timeMultiplier = 1;
    // Eliminado el ticker de tiempo basado en reloj real
  }

  getHospitalTime() {
    return new Date(); // Fallback, no se usa para visual themes
  }

  syncVisualTheme(guardsToday = 0) {
    // Don't auto-switch day/night themes while a case is active
    // encounter), to avoid jarring background flips mid-case and to preserve
    // the special library/void themes.
    if (this.currentState === GAME_STATES.ENCOUNTER) return;
    
    let targetTheme = 'theme-morning';
    if (guardsToday === 1) targetTheme = 'theme-afternoon';
    else if (guardsToday >= 2) targetTheme = 'theme-night';
    
    if (!document.body.classList.contains(targetTheme)) {
        logger.info(`[TIME] Guards Today: ${guardsToday}. Switching theme to ${targetTheme}`);
        document.body.classList.remove('theme-morning', 'theme-afternoon', 'theme-night');
        document.body.classList.add(targetTheme);
    }
  }

  changeState(newState, data = {}) {
    logger.debug(`[STATE CHANGE] ${this.currentState} -> ${newState}`, { data });
    const prevState = this.currentState;
    this.currentState = newState;

    // Save current encounter theme before resetting (used in FEEDBACK to preserve day/night cycle)
    // Reset day/night cycle themes
    const cycleThemes = ['theme-morning', 'theme-afternoon', 'theme-night'];
    const currentCycleTheme = cycleThemes.find(t => document.body.classList.contains(t));

    // UI Elements for Scenes
    const sceneHeader = document.getElementById('scene-header');
    const sceneTitle = document.getElementById('scene-title');
    const sceneBack = document.getElementById('scene-back');
    const specialtyScreen = document.getElementById('specialty-selection-screen');
    const reportScreen = document.getElementById('report-screen');

    // Reset all areas
    logger.debug("[STATE] Hiding all UI areas...");
    this.ui.menuArea.style.display = 'none';
    this.ui.gameArea.style.display = 'none';
    if (this.ui.hubModal) this.ui.hubModal.style.display = 'none';
    if (this.ui.caseModal) this.ui.caseModal.style.display = 'none';
    if (specialtyScreen) specialtyScreen.style.display = 'none';
    if (reportScreen) reportScreen.style.display = 'none';
    if (sceneHeader) sceneHeader.style.display = 'none';
    if (sceneBack) sceneBack.style.display = 'none';

    // Clean up body classes
    document.body.classList.remove('lethal-mode');
    const leavingEncounter = newState === GAME_STATES.MENU || newState === GAME_STATES.REPORT;
    
    if (leavingEncounter) {
      document.body.classList.remove('theme-void', 'theme-library');
      this.syncVisualTheme(data.guardsToday || 0);
    }

    // UI Logic Shell
    if (newState === GAME_STATES.MENU || newState === GAME_STATES.REPORT) {
       if (this.callbacks.onEnterMenu) this.callbacks.onEnterMenu();
    }

    if (newState === GAME_STATES.ENCOUNTER) {
      if (this.ui.learningNote) {
        this.ui.learningNote.style.display = 'none';
        this.ui.learningNote.innerHTML = '';
      }
      if (this.callbacks.onEnterEncounter) this.callbacks.onEnterEncounter();
    }

    // State Router
    switch (newState) {
      case GAME_STATES.MENU:
        logger.debug("[STATE] Showing menu area");
        this.ui.menuArea.style.display = 'flex';
        if (sceneHeader) {
          sceneHeader.style.display = 'flex';
          sceneTitle.textContent = 'TABLÓN DE GUARDIA';
          sceneBack.style.display = 'none';
        }
        break;
      case GAME_STATES.ENCOUNTER:
        logger.debug("[STATE] Showing game/encounter area");
        this.ui.gameArea.style.display = 'block';
        if (sceneHeader) {
          sceneHeader.style.display = 'flex';
          sceneTitle.textContent = data.isLibrary ? 'SALA DE ESTUDIO' : 'EN GUARDIA';
          sceneBack.style.display = 'none';
        }
        if (data.isLibrary) {
            document.body.classList.remove('theme-morning', 'theme-afternoon', 'theme-night');
            document.body.classList.add('theme-library');
        } else {
            this.syncVisualTheme(data.guardsToday || 0);
        }
        break;
      case GAME_STATES.FEEDBACK:
        logger.debug("[STATE] Showing feedback area");
        this.ui.gameArea.style.display = 'block';
        if (sceneHeader) {
          sceneHeader.style.display = 'flex';
          sceneBack.style.display = 'none';
        }
        break;
      case GAME_STATES.REPORT:
        logger.debug("[STATE] Showing report screen");
        if (reportScreen) reportScreen.style.display = 'block';
        if (sceneHeader) {
          sceneHeader.style.display = 'flex';
          sceneTitle.textContent = 'REPORTE FINAL';
          sceneBack.style.display = 'none';
        }
        break;
      case GAME_STATES.CASE_SELECT:
        logger.debug("[STATE] Showing case selection modal");
        this.ui.caseModal.style.display = 'flex';
        if (this.callbacks.renderCaseSelection) this.callbacks.renderCaseSelection();
        break;
      case GAME_STATES.SPECIALTY_SELECT:
        logger.debug("[STATE] Showing specialty selection screen");
        if (specialtyScreen) specialtyScreen.style.display = 'block';
        if (sceneHeader) {
          sceneHeader.style.display = 'flex';
          sceneTitle.textContent = 'MAPA DEL HOSPITAL';
          sceneBack.style.display = 'block';
        }
        break;
      case 'END_OF_DAY':
        logger.debug("[STATE] Showing end of day screen");
        const eodScreen = document.getElementById('end-of-day-screen');
        if (eodScreen) eodScreen.style.display = 'block';
        if (sceneHeader) {
          sceneHeader.style.display = 'flex';
          sceneTitle.textContent = 'CIERRE DE JORNADA';
          sceneBack.style.display = 'none';
        }
        break;
    }
  }
}
